'use client';

import { useEffect, useRef, useState } from 'react';
import { usePathname, useSearchParams } from 'next/navigation';
import { signIn, useSession } from 'next-auth/react';
import { useSessionStore } from '@/store/sessionStore';
import { api } from '@/shared/lib/api';
import { LoginStatus as LoginStatus } from '@/features/session/enums/LoginStatus';
import {
  isTokenExpired,
  msUntilExpiry,
  forceLogout,
  REFRESH_AHEAD_OF_EXPIRY_MS,
} from '@/features/session/lib/sessionExpiry';

export function useLoginLayout() {
  const { sessionId, token, createSession, loadSession, clearSession, refreshToken } = useSessionStore();
  const { status: authStatus } = useSession();
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const [isInitialized, setIsInitialized] = useState(false);
  const [isHydrated, setIsHydrated] = useState(false);
  const [status, setStatus] = useState<LoginStatus>(LoginStatus.ANONYMOUS);
  const previousAuthStatus = useRef<string | null>(null);
  const isInitCallbackUrl = pathname === '/' && searchParams.get('status') === 'init';

  useEffect(() => {
    setIsHydrated(true);
  }, []);

  useEffect(() => {
    if (!isHydrated || authStatus === 'loading') {
      setStatus(LoginStatus.LOADING);
      return;
    }

    if (isInitCallbackUrl && authStatus !== 'authenticated') {
      setIsInitialized(false);
      setStatus(LoginStatus.INITIALIZING);
      previousAuthStatus.current = authStatus;
      return;
    }

    if (authStatus === 'unauthenticated') {
      if (sessionId) {
        clearSession();
      }
      setIsInitialized(false);
      setStatus(LoginStatus.ANONYMOUS);
      previousAuthStatus.current = authStatus;
      return;
    }

    if (authStatus !== 'authenticated') {
      previousAuthStatus.current = authStatus;
      return;
    }

    const didJustLogin = previousAuthStatus.current !== 'authenticated';

    if (!didJustLogin && !sessionId) {
      setIsInitialized(false);
      setStatus(LoginStatus.ANONYMOUS);
      previousAuthStatus.current = authStatus;
      return;
    }

    // Keep API client ready after refresh without re-initializing backend session.
    if (!didJustLogin && sessionId) {
        // Token expirado ao refrescar: não repor a sessão — forçar logout completo.
        if (isTokenExpired(token)) {
            forceLogout();
            previousAuthStatus.current = authStatus;
            return;
        }
        api.setSessionId(sessionId);
        // Repõe o JWT persistido para os pedidos autenticados continuarem a funcionar.
        if (token) api.setSessionToken(token);
        setIsInitialized(true);
        setStatus(LoginStatus.AUTHENTICATED);
        previousAuthStatus.current = authStatus;
        return;
    }

    setStatus(LoginStatus.INITIALIZING);

    const initSession = async () => {
      // Reaproveita a sessão persistida apenas se o token ainda for válido; caso
      // contrário cria uma nova (o utilizador acabou de autenticar — não faz sentido
      // fazer logout de um token expirado que sobrou de uma sessão anterior).
      if (sessionId && !isTokenExpired(token)) {
        try {
          api.setSessionId(sessionId);
          await loadSession(sessionId);
        } catch {
          await createSession();
        }
      } else {
        await createSession();
      }
      setIsInitialized(true);
      setStatus(LoginStatus.AUTHENTICATED);
      previousAuthStatus.current = authStatus;
    };

    initSession();
  }, [isHydrated, authStatus, sessionId, token, createSession, loadSession, clearSession, isInitCallbackUrl]);

  // Timer proativo: agenda a renovação do JWT de sessão com antecedência sobre a
  // expiração, para o utilizador não sofrer logout automático enquanto mantiver o
  // separador aberto. Ao ter sucesso, o novo token entra no store e este efeito
  // reagenda-se sozinho (dependência de `token`) para a próxima janela de refresh.
  // O logout só acontece se o refresh falhar (ex. limite de vida máximo da sessão
  // excedido — ver session_token_manager.refresh_session_token) ou se o token já
  // tiver expirado sem chegar a ser renovado.
  useEffect(() => {
    if (status !== LoginStatus.AUTHENTICATED) return;
    const remaining = msUntilExpiry(token);
    if (remaining === null) return;
    if (remaining <= 0) {
      forceLogout();
      return;
    }

    const delay = Math.max(0, remaining - REFRESH_AHEAD_OF_EXPIRY_MS);
    const timeoutId = setTimeout(async () => {
      try {
        await refreshToken();
      } catch {
        forceLogout();
      }
    }, delay);
    return () => clearTimeout(timeoutId);
  }, [status, token, refreshToken]);

  const handleLogin = () => {
    signIn(
        'azure-ad',
        {
        callbackUrl: '/',
        redirect: true,
        },
        {
        prompt: 'select_account',
        }
    );
  }
    

  return {
    status,
    isInitialized,
    handleLogin,
  };
}
